import React, { useRef, useState } from 'react';
import { FileImage, Download } from 'lucide-react';
import useServicesService from '../../../services/useServicesService';
import showToast from '../../../utils/toast';
import './styles/ImageToExcelConverter.css';

type Props = {
  compact?: boolean;
};

const ImageToExcelConverter: React.FC<Props> = ({ compact = false }) => {
  const { convertImageToExcel, fetching } = useServicesService();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);

  const handleFile = (file: File | null) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      showToast('Use una imagen JPG, PNG o WebP de la planilla', 'error');
      return;
    }
    setLastFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleConvert = () => {
    if (!lastFile) {
      showToast('Seleccione una imagen primero', 'error');
      return;
    }
    convertImageToExcel(lastFile, {
      onSuccess: (resp) => {
        showToast(resp?.message ?? 'Excel descargado', 'success');
      },
      onError: (err) => {
        showToast(err?.message ?? 'Error al convertir la imagen', 'error');
      },
    });
  };

  return (
    <div className={`image-to-excel${compact ? ' image-to-excel--compact' : ''}`}>
      <div
        className="image-to-excel-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0] ?? null);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        <FileImage size={32} strokeWidth={1.5} aria-hidden />
        <p>Arrastre o seleccione la captura de la planilla El Abra</p>
        <span className="image-to-excel-hint">JPG, PNG o WebP · máx. 12 MB</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          handleFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      {previewUrl && (
        <div className="image-to-excel-preview">
          <img src={previewUrl} alt="Vista previa de la planilla" />
        </div>
      )}

      <button
        type="button"
        className="image-to-excel-btn"
        onClick={handleConvert}
        disabled={!lastFile || fetching.imageToExcel}
      >
        <Download size={18} aria-hidden />
        {fetching.imageToExcel ? 'Generando Excel…' : 'Convertir a Excel'}
      </button>
    </div>
  );
};

export default ImageToExcelConverter;
